import { Eye, Compass } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { WHATSAPP_LINK, DEFAULT_WA_MESSAGE } from "@/lib/site";
import { Link } from "react-router-dom";

export default function VisionMission() {
  return (
    <div data-testid="vision-mission-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-brand-blue via-brand-ocean to-brand-green" />
        <div className="grain absolute inset-0 z-0" />
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8">
          <Overline className="text-white/80">Our Guiding Principles</Overline>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-4xl text-5xl md:text-7xl font-bold tracking-tight text-white leading-[1.02]"
          >
            Vision &amp; Mission
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="mt-6 max-w-2xl text-lg md:text-xl text-white/85 leading-relaxed"
          >
            The compass that guides every itinerary we build and every relationship we nurture.
          </motion.p>
        </div>
      </section>

      {/* Panels */}
      <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-24 md:py-32">
        <div className="grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="relative h-full overflow-hidden rounded-3xl border border-brand-blue/10 bg-white p-10 md:p-14">
              <span className="pointer-events-none absolute -top-6 -right-4 font-heading text-[8rem] font-extrabold text-brand-blue/[0.05]">01</span>
              <div className="inline-flex rounded-2xl bg-gradient-to-br from-brand-blue to-brand-ocean p-4 text-white">
                <Eye size={28} />
              </div>
              <h2 className="mt-7 text-4xl font-bold tracking-tight text-brand-blue">Our Vision</h2>
              <p className="mt-6 text-lg text-brand-slate leading-relaxed">
                To be the most trusted corporate travel partner globally, recognized for our
                precision, innovation, and unwavering commitment to business mobility. We aim to
                make every journey productive, seamless, and strategically aligned with business
                growth.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative h-full overflow-hidden rounded-3xl bg-gradient-to-br from-brand-blue to-brand-green p-10 md:p-14 text-white">
              <span className="pointer-events-none absolute -top-6 -right-4 font-heading text-[8rem] font-extrabold text-white/[0.08]">02</span>
              <div className="inline-flex rounded-2xl bg-white/15 p-4 backdrop-blur">
                <Compass size={28} />
              </div>
              <h2 className="mt-7 text-4xl font-bold tracking-tight">Our Mission</h2>
              <p className="mt-6 text-lg text-white/90 leading-relaxed">
                To empower businesses through meticulously crafted travel solutions tailored for
                corporate needs. We deliver reliability, flexibility, and efficiency in ticketing,
                visas, documentation, and executive support, enabling professionals to focus on
                their goals while we manage the logistics with integrity and excellence.
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" data-testid="vm-contact-btn" className="inline-flex items-center justify-center rounded-full bg-brand-blue px-8 py-4 font-semibold text-white hover:bg-brand-ocean transition-colors">
              Partner With Us
            </Link>
            <a href={WHATSAPP_LINK(DEFAULT_WA_MESSAGE)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center rounded-full border-2 border-brand-green px-8 py-4 font-semibold text-brand-green hover:bg-brand-green hover:text-white transition-colors">
              Chat on WhatsApp
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
